import Image from 'next/image'
import Link from 'next/link'
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'

interface HomeBaProps {
	heading?: {
		title?: string
	}
	text?: {
		text?: string
	}
	background_desktop?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_download: string
			}
		}
	}
	background_mobile?: {
		alt: string
		image_file: {
			image: {
				id: string
				filename_download: string
			}
		}
	}
	content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					menu_items?: {
						menu_items_id: {
							label?: string
							href?: string
						}
					}[]
				}
			}[]
		}
	}[]
	button?: {
		href: string
		label: string
	}
}

export const fields = [
	'heading.title',
	'text.text',

	'background_desktop.alt',
	'background_desktop.image_file.image.id',
	'background_desktop.image_file.image.filename_download',
	'background_mobile.alt',
	'background_mobile.image_file.image.id',
	'background_mobile.image_file.image.filename_download',

	'content_collection.site_content_collections_id.collection.item:menus.menu_items.menu_items_id',
	'content_collection.site_content_collections_id.collection.item:menus.menu_items.menu_items_id.label',
	'content_collection.site_content_collections_id.collection.item:menus.menu_items.menu_items_id.href',
	'button.href',
	'button.label',
	'*',
] as const

export default function HomeBa({ data }: { data: HomeBaProps }) {
	const desktopImageSrc =
		data?.background_desktop?.image_file?.image?.id &&
		data?.background_desktop?.image_file?.image?.filename_download
			? getReadableImageUrl(
					data.background_desktop.image_file.image.id,
					data.background_desktop.image_file.image.filename_download
			  )
			: ''
	const mobileImageSrc =
		data?.background_mobile?.image_file?.image?.id &&
		data?.background_mobile?.image_file?.image?.filename_download
			? getReadableImageUrl(
					data.background_mobile.image_file.image.id,
					data.background_mobile.image_file.image.filename_download
			  )
			: desktopImageSrc
	const imageAlt = data?.background_mobile?.alt || data?.background_desktop?.alt || ''

	return (
		<>
			<section className='relative overflow-hidden py-[3rem] lg:hidden block'>
				<div className='container relative'>
					<div className='space-y-4'>
						<div className='font-agopus text-4xl text-black'>
							{data?.heading?.title || ''}
						</div>
						<div
							className='prose font-brandon text-xl leading-loose'
							dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || '')}
						/>
					</div>
				</div>
			</section>
			<section className='HomeBa relative overflow-hidden py-[8rem] max-md:py-[2rem] max-md:h-[800px] max-md:flex max-md:items-end max-md:rounded-tr-[50px] max-md:rounded-br-[50px]'>
				{/* Background Image */}
				{mobileImageSrc ? (
					<Image
						src={mobileImageSrc}
						alt={imageAlt}
						fill
						className='object-cover object-top min-[580px]:hidden'
						priority
						sizes='(max-width: 36.25rem) 100vw'
					/>
				) : null}
				{desktopImageSrc ? (
					<Image
						src={desktopImageSrc}
						alt={imageAlt}
						fill
						className='hidden object-cover object-top min-[580px]:block md:top-[100px!important]'
						priority
						sizes='(min-width: 36.25rem) 100vw'
					/>
				) : null}

				{/* Overlay for mobile */}
				<div className='absolute inset-0 bg-gradient-to-b from-transparent via-[#000000]/40 to-[#000000]/60 sm:bg-gradient-to-r sm:from-white/60 sm:via-white/30 sm:to-transparent md:hidden'></div>

				<div className='container relative lg:-mt-[8rem]'>
					<div className='flex flex-col lg:flex-row'>
						{/* Content Box */}
						<div className='box_txt w-full max-w-[43.75rem] lg:bg-white lg:rounded-[4.125rem] lg:p-16 flex flex-col gap-8 lg:gap-16'>
							{/* Desktop Heading */}
							<div className='space-y-4 lg:block hidden'>
								<h2 className='font-agopus text-5xl text-gray-slate'>
									{data?.heading?.title || ''}
								</h2>
								<div
									className='[&>p]:text-xl [&>p]:leading-loose grid gap-8'
									dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || '')}
								/>
							</div>

							{/* Procedures Tags */}
							<div className='flex flex-wrap gap-2.5 mt-auto'>
								{data?.content_collection?.map(collection =>
									collection.site_content_collections_id.collection[0].item.menu_items?.map(
										menuItem => (
											<a
												key={menuItem.menu_items_id.href}
												href={menuItem.menu_items_id.href || ''}
												className='px-5 py-2 border md:border-sand md:text-gray-slate text-white border-white rounded-[2.375rem] text-xl tracking-wide inline-block hover:bg-sand/10 transition-colors cursor-pointer'
											>
												{menuItem.menu_items_id.label || ''}
											</a>
										)
									)
								)}
							</div>

							{/* CTA Button */}
							{data?.button?.href && data?.button?.label && (
								<div className='w-fit max-md:w-full'>
									<Link
										href={data.button.href}
										className='inline-block px-12 py-5 bg-brown rounded-lg text-xl text-white font-brandon hover:bg-brown/90 transition-colors w-full text-center'
									>
										{data.button.label}
									</Link>
								</div>
							)}
						</div>
					</div>
				</div>
				{/* Brand Logo */}
				<div className='absolute right-6 top-1/2 lg:-translate-y-1/2 lg:block max-lg:top-[70px] max-lg:left-6 max-lg:right-auto'>
					<Image
						src='/images/hfps-brand-vertical.svg'
						alt='HFPS Brand'
						width={25}
						height={198}
						className='max-lg:w-[15px]'
					/>
				</div>
				{/* Model Text */}
				<div className='absolute top-6 left-6 max-lg:text-white max-lg:top-[10px] max-lg:left-6 lg:bottom-6 lg:right-6 lg:left-auto lg:top-auto lg:text-black font-brandon font-medium tracking-wide text-lg'>
					Model
				</div>
			</section>
		</>
	)
}